import { useCallback, useEffect, useState } from 'react';
import type {
  ConnectionDef,
  PolicySpec,
  ResolvedStep,
  SlackChannelBinding,
  StepConfig,
} from '@reef/schema';
import { api, connectionName, type ConnectionSummary, type SlackChannelSummary } from '../api.ts';
import {
  DUAL_IDENTITY,
  IdentityBadge,
  botLabelFor,
  identityKindFor,
  identityTip,
} from './IdentityBadge.tsx';

/**
 * Everything about one step, in two halves.
 *
 * The top half is what this step *declares* — the editable document. The bottom
 * half is what it will *actually run under* once inheritance is applied. Keeping
 * both on screen is the point: a step whose declared policy looks permissive but
 * whose resolved policy caps it at ten rows is the single most confusing thing
 * about a nested model, and showing the resolved view removes the guesswork.
 */
export function StepInspector({
  step,
  workflowId,
  connections,
  policies,
  skills,
  editable,
  onChange,
}: {
  step: StepConfig;
  workflowId: string;
  connections: ConnectionSummary[];
  policies: PolicySpec[];
  skills: string[];
  editable: boolean;
  onChange(next: StepConfig): void;
}) {
  const [resolved, setResolved] = useState<ResolvedStep | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResolved(null);
    setResolveError(null);

    api
      .resolvedStep(workflowId, step.id)
      .then((result) => !cancelled && setResolved(result.resolved))
      .catch((error) => !cancelled && setResolveError((error as Error).message));

    return () => {
      cancelled = true;
    };
  }, [
    workflowId,
    step.id,
    step.mcpConnections,
    step.networkAllowlist,
    step.policy,
    step.runtime,
    step.toolsAllowed,
  ]);

  const patch = (changes: Partial<StepConfig>) => onChange({ ...step, ...changes });

  return (
    <div className="stack">
      <div>
        <p className="section-title">Step</p>
        <div className="stack">
          <div className="field">
            <label htmlFor="step-name">Name</label>
            <input
              id="step-name"
              value={step.name}
              disabled={!editable}
              onChange={(event) => patch({ name: event.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="step-prompt">Instructions</label>
            <textarea
              id="step-prompt"
              rows={8}
              value={step.prompt}
              disabled={!editable}
              onChange={(event) => patch({ prompt: event.target.value })}
            />
            <span className="hint">
              This is the agent's system prompt. The code-mode API surface and the policy summary
              are appended automatically.
            </span>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-runtime">Runtime</label>
              <select
                id="step-runtime"
                value={step.runtime ?? ''}
                disabled={!editable}
                onChange={(event) =>
                  patch({ runtime: (event.target.value || undefined) as StepConfig['runtime'] })
                }
              >
                <option value="">inherit</option>
                <option value="isolate">isolate</option>
                <option value="container">container</option>
              </select>
            </div>

            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-policy">Policy</label>
              <select
                id="step-policy"
                value={typeof step.policy === 'string' ? step.policy : ''}
                disabled={!editable}
                onChange={(event) => patch({ policy: event.target.value || undefined })}
              >
                <option value="">inherit</option>
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Approval</label>
            <label className="row">
              <input
                type="checkbox"
                checked={step.requiresApproval ?? false}
                disabled={!editable}
                onChange={(event) => patch({ requiresApproval: event.target.checked })}
              />
              <span>Pause for a human before committing this step's work</span>
            </label>
            {step.requiresApproval && (
              <>
                <input
                  placeholder="#eng-approvals"
                  value={step.approvalChannel ?? ''}
                  disabled={!editable}
                  onChange={(event) => patch({ approvalChannel: event.target.value })}
                />
                {/*
                  Where else it goes, right beside where the document sends it.
                  Somebody setting "this needs approval" is already deciding who
                  sees it; a separate screen for the second half is a screen
                  they will not find. And the two halves are genuinely
                  different — this one is a row, changeable by the team who
                  reads the channel, where the field above is a commit.
                */}
                <SlackReviewChannels workflowId={workflowId} editable={editable} />
              </>
            )}
          </div>

          <div className="field">
            <label>Connections this step adds</label>
            <div className="row wrap">
              {connections.map((connection) => {
                const mounted = step.mcpConnections?.some((ref) => ref.id === connection.id);
                return (
                  <button
                    key={connection.id}
                    className={mounted ? 'primary' : ''}
                    disabled={!editable}
                    onClick={() =>
                      patch({
                        mcpConnections: mounted
                          ? step.mcpConnections!.filter((ref) => ref.id !== connection.id)
                          : [
                              ...(step.mcpConnections ?? []),
                              { id: connection.id, as: suggestMount(connection) },
                            ],
                      })
                    }
                    title={
                      DUAL_IDENTITY.has(connection.credentialMode)
                        ? `${connection.name} can run as you or as ${botLabelFor(connection)} — mount it to choose.`
                        : connection.credentialMode === 'service'
                          ? `${connection.name} always runs on the shared workspace credential.`
                          : `${connection.name} always runs on your own account.`
                    }
                  >
                    {connection.name}
                    {/*
                      Marks the connections that have an identity to choose, so
                      the decision is discoverable before mounting rather than
                      appearing as a surprise control afterwards.
                    */}
                    {DUAL_IDENTITY.has(connection.credentialMode) && (
                      <span aria-hidden="true" style={{ opacity: 0.7 }}>
                        {' '}
                        ⇄
                      </span>
                    )}
                    {!connection.authorized && ' ⚠'}
                  </button>
                );
              })}
            </div>
            <span className="hint">
              <span aria-hidden="true">⇄</span> marks a connection that can run as you or as a bot.
              Connections inherited from the workflow and from parent steps are always present and
              cannot be removed here.
            </span>
          </div>

          {/*
            Only connections that actually hold two credentials get this control.
            Whose name ends up on a comment is a decision the author makes here,
            in the document — never something the step's prompt can talk its way
            into changing.
          */}
          {(step.mcpConnections ?? [])
            .map((ref) => ({ ref, def: connections.find((entry) => entry.id === ref.id) }))
            .filter(({ def }) => def && DUAL_IDENTITY.has(def.credentialMode))
            .map(({ ref, def }) => {
              const actor = ref.actor ?? 'auto';
              const kind = identityKindFor(actor);
              const bot = botLabelFor(def!);

              return (
                <div className="field" key={`actor-${ref.id}`}>
                  <div className="row">
                    <label htmlFor={`actor-${ref.id}`} style={{ flex: 1 }}>
                      Run {def!.name} as
                    </label>
                    {/*
                      The badge repeats what the select already says, on purpose:
                      it is the same mark that appears on the step in the tree and
                      against every entry in the audit log, so the three read as
                      one fact rather than three descriptions.
                    */}
                    <IdentityBadge
                      kind={kind}
                      label={kind === 'person' ? 'you' : kind === 'bot' ? bot : 'you or bot'}
                      tip={identityTip(kind, bot)}
                    />
                  </div>
                  <select
                    id={`actor-${ref.id}`}
                    value={actor}
                    disabled={!editable}
                    onChange={(event) =>
                      patch({
                        mcpConnections: step.mcpConnections!.map((entry) =>
                          entry.id === ref.id
                            ? { ...entry, actor: event.target.value as typeof entry.actor }
                            : entry,
                        ),
                      })
                    }
                  >
                    <option value="auto">Whoever triggered it — {bot} when nobody did</option>
                    <option value="user">The person running it, always</option>
                    <option value="service">{bot}, always</option>
                  </select>
                  <span className="hint">
                    {actor === 'user'
                      ? `Uses your own ${def!.name} account, so the work carries your name — and a run nobody triggered will stop and ask for a connection instead of falling back to ${bot}.`
                      : actor === 'service'
                        ? `Always posts as ${bot}, even when a person pressed Run. Nobody needs to link their own ${def!.name} account for this step.`
                        : `Your account for anything a person starts; ${bot} for scheduled and webhook runs, so they keep working after you log off.`}
                  </span>
                </div>
              );
            })}

          <div className="field">
            <label>Skills</label>
            <div className="row wrap">
              {skills.map((skill) => {
                const attached = step.skills?.some((entry) => entry.name === skill);
                return (
                  <button
                    key={skill}
                    className={attached ? 'primary' : ''}
                    disabled={!editable}
                    onClick={() =>
                      patch({
                        skills: attached
                          ? step.skills!.filter((entry) => entry.name !== skill)
                          : [...(step.skills ?? []), { name: skill }],
                      })
                    }
                  >
                    {skill}
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Network is a widening control, not a narrowing one: resolution takes
            the union of the workflow's allowlist and every ancestor's, so a step
            can reach further than its parent but can never pull an inherited
            host back. The hint says so, because a "none" option here would be a
            switch that silently does nothing.
          */}
          <div className="field">
            <label htmlFor="step-network">Network access</label>
            <input
              id="step-network"
              value={(step.networkAllowlist ?? []).join(', ')}
              placeholder="inherited only — e.g. api.github.com, files.internal"
              disabled={!editable}
              onChange={(event) => {
                const hosts = event.target.value
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter(Boolean);
                // Absent rather than empty, so a step that adds nothing adds
                // nothing to the committed document either.
                patch({ networkAllowlist: hosts.length ? hosts : undefined });
              }}
            />
            <span className="hint">
              Comma-separated hostnames this step may reach directly, on top of whatever the
              workflow and its parent steps already allow. Leave it empty to inherit and nothing
              else — everything then goes through a connection.
            </span>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-runtime-limit">Max runtime (min)</label>
              <input
                id="step-runtime-limit"
                type="number"
                min={1}
                value={Math.round((step.limits?.maxRuntimeMs ?? 3_600_000) / 60_000)}
                disabled={!editable}
                onChange={(event) =>
                  patch({
                    limits: {
                      maxSpendCents: step.limits?.maxSpendCents ?? 10_000,
                      maxRuntimeMs: Number(event.target.value) * 60_000,
                    },
                  })
                }
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="step-spend-limit">Max spend ($)</label>
              <input
                id="step-spend-limit"
                type="number"
                min={1}
                value={Math.round((step.limits?.maxSpendCents ?? 10_000) / 100)}
                disabled={!editable}
                onChange={(event) =>
                  patch({
                    limits: {
                      maxRuntimeMs: step.limits?.maxRuntimeMs ?? 3_600_000,
                      maxSpendCents: Number(event.target.value) * 100,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />

      <div>
        <p className="section-title">What will actually run</p>

        {resolveError && (
          <div className="banner danger">
            <h3>This step cannot be resolved</h3>
            <p style={{ margin: 0 }}>{resolveError}</p>
          </div>
        )}

        {!resolved && !resolveError && <p className="hint">Resolving…</p>}

        {resolved && (
          <div className="stack">
            <div className="row wrap">
              <span className="pill accent">level {resolved.level} of 3</span>
              <span className={`pill ${resolved.runtime === 'container' ? 'warn' : ''}`}>
                {resolved.runtime}
              </span>
              <span className="pill">
                ${(resolved.limits.maxSpendCents / 100).toFixed(0)} ·{' '}
                {Math.round(resolved.limits.maxRuntimeMs / 60_000)} min
              </span>
            </div>

            <div className="field">
              <label>Connections after inheritance</label>
              {resolved.connections.length === 0 ? (
                <span className="hint">None. This step has no data access.</span>
              ) : (
                <div className="row wrap">
                  {resolved.connections.map((connection) => (
                    <span
                      key={connection.as}
                      className="pill accent"
                      title={`Mounted as ${connection.as}`}
                    >
                      {connectionName(connections, connection.id)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="field">
              <label>Network</label>
              <span className="hint">
                {resolved.networkAllowlist.length === 0
                  ? 'No outbound network. Everything goes through a connection.'
                  : resolved.networkAllowlist.join(', ')}
              </span>
            </div>

            <div className="field">
              <label>Policy chain</label>
              <div className="row wrap">
                {resolved.policyChain.length === 0 ? (
                  <span className="hint">No policy — the workspace defaults apply.</span>
                ) : (
                  resolved.policyChain.map((entry, index) => (
                    <span key={index} className="pill">
                      {typeof entry === 'string' ? entry : entry.id}
                    </span>
                  ))
                )}
              </div>
              <span className="hint">
                Merged left to right, always tightening: a child can narrow what its parent allowed
                but never widen it.
              </span>
            </div>

            {resolved.toolsAllowed && (
              <div className="field">
                <label>Tool allowlist</label>
                <div className="row wrap">
                  {resolved.toolsAllowed.map((tool) => (
                    <span key={tool} className="pill">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** A readable default mount name — `db`, `github` — derived from the connection. */
function suggestMount(connection: ConnectionDef): string {
  const base = connection.provider === 'postgres' ? 'db' : connection.provider;
  return base.replace(/[^a-zA-Z0-9]/g, '').replace(/^[0-9]+/, '') || 'conn';
}



/**
 * The channels this workflow's reviews *also* reach.
 *
 * The field above it is `approvalChannel` — one string, committed to git,
 * changeable only by somebody with edit rights on the workflow. That is the
 * right bar for what a step may do and the wrong one for where it talks: the
 * team who wants the channel changed is usually not the team who can commit,
 * so the request sits in a backlog while approvals land somewhere nobody reads.
 *
 * A binding is a row instead. It shows here because this is where the decision
 * is being made, and it says plainly that anybody standing in a channel can add
 * one with `/reef connect` — which is the path that does not need this screen
 * at all.
 */
function SlackReviewChannels({
  workflowId,
  editable,
}: {
  workflowId: string;
  editable: boolean;
}) {
  const [bindings, setBindings] = useState<SlackChannelBinding[]>([]);
  const [channels, setChannels] = useState<SlackChannelSummary[]>([]);
  const [configured, setConfigured] = useState(true);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const settings = await api.slackSettings(workflowId);
      setBindings(settings.bindings);
      setConfigured(settings.configured);
      if (settings.configured) {
        setChannels((await api.listSlackBotChannels()).channels);
      }
    } catch {
      // Silent: this is a secondary control on somebody else's screen, and a
      // red banner here for a Slack that is simply absent would read as a
      // problem with the step they are editing.
      setConfigured(false);
    }
  }, [workflowId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!configured) return null;

  const relevant = bindings.filter(
    (binding) => binding.purpose === 'all' || binding.purpose === 'approvals',
  );

  const add = async () => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const channel = channels.find((entry) => entry.id === choice);
      await api.bindSlackChannel({
        channelId: choice,
        ...(channel ? { channelName: channel.name } : {}),
        workflowId,
        purpose: 'approvals',
      });
      setChoice('');
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (binding: SlackChannelBinding) => {
    setBusy(true);
    setError(null);
    try {
      await api.unbindSlackChannel(binding.channelId, binding.workflowId);
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ marginTop: '0.5rem' }}>
      <p className="hint" style={{ margin: 0 }}>
        Also posted to
        {relevant.length === 0 && ' — nothing yet.'}
      </p>

      {relevant.length > 0 && (
        <div className="row wrap">
          {relevant.map((binding) => (
            <span className="chip" key={binding.id}>
              {binding.channelName ? `#${binding.channelName}` : binding.channelId}
              {/*
                A workspace-wide binding is shown here because it does reach
                this workflow — but it is not this workflow's to remove, and
                a Remove button that 403s would be worse than none.
              */}
              {binding.workflowId === null ? (
                <em style={{ marginLeft: '0.35rem', fontStyle: 'normal', opacity: 0.7 }}>
                  (all workflows)
                </em>
              ) : (
                editable && (
                  <button
                    className="chip-x"
                    onClick={() => void remove(binding)}
                    disabled={busy}
                    aria-label={`Stop posting to ${binding.channelName ?? binding.channelId}`}
                  >
                    ×
                  </button>
                )
              )}
            </span>
          ))}
        </div>
      )}

      {editable && (
        <div className="row">
          <select value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="">add a channel…</option>
            {channels
              .filter((channel) => !relevant.some((binding) => binding.channelId === channel.id))
              .map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.private ? '🔒 ' : '# '}
                  {channel.name}
                </option>
              ))}
          </select>
          <button className="ghost" onClick={() => void add()} disabled={!choice || busy}>
            Add
          </button>
        </div>
      )}

      {/* `--danger` rather than a `.banner`: this is a secondary control inside
          a field, and a full-width error block here would out-shout the step
          being edited. */}
      {error && (
        <p className="hint" style={{ margin: 0, color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      <p className="hint" style={{ margin: 0 }}>
        Anyone can add the channel they are standing in with <code>/reef connect</code>, and a
        reviewer can have their own copy DM'd with <code>/reef notify</code>. A decision made
        anywhere closes the card everywhere.
      </p>
    </div>
  );
}
